import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#FCFAF8] flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <span className="inline-block bg-[#FFF5F5] text-[#D48585] text-xs font-body font-semibold px-3 py-1 rounded-full mb-4">Legal</span>
        <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] mb-2" data-testid="terms-title">Terms &amp; Conditions</h1>
        <p className="font-body text-sm text-[#A89F91] mb-8">Last updated: April 2026</p>

        <div className="prose font-body text-[#4A4745] text-sm leading-relaxed space-y-6">
          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">1. Acceptance of Terms</h2>
            <p>
              By accessing or using {'{'}Beauty{'}'} Stashmart ("the Platform"), you agree to be bound by these Terms and Conditions.
              If you do not agree with any part of these terms, you must not use the Platform.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">2. Eligibility</h2>
            <p>
              You must be at least <strong>18 years of age</strong> to use {'{'}Beauty{'}'} Stashmart. If you are under 18, you may only use the Platform
              with the consent and supervision of a parent or legal guardian, who agrees to be bound by these Terms on your behalf.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">3. Platform Role &amp; Disclaimer</h2>
            <p>
              {'{'}Beauty{'}'} Stashmart is a <strong>consumer-to-consumer (C2C) marketplace</strong> that connects individual buyers and sellers.
              {'{'}Beauty{'}'} Stashmart is <em>not</em> the owner, seller, or distributor of any products listed on the Platform. We do not inspect,
              verify the quality of, or guarantee any product listed by users.
            </p>
            <p>
              All transactions occur directly between the buyer and the seller. {'{'}Beauty{'}'} Stashmart is not a party to any transaction and
              is not responsible for the fulfillment, quality, safety, legality, or authenticity of any listed product. Users transact at
              their own risk.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">4. Seller Obligations</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                Sellers must describe products <strong>honestly and accurately</strong>, including the product condition (new, lightly used, half used, etc.),
                remaining amount, expiry date, and any defects.
              </li>
              <li>
                Sellers are solely responsible for the <strong>authenticity</strong> of the products they list. Listing counterfeit or fake products is strictly prohibited.
              </li>
              <li>
                Sellers must not list <strong>expired products</strong> (past the expiry date printed on the packaging).
              </li>
              <li>
                Sellers must fulfill orders promptly after payment is confirmed, either by shipping the product via courier or arranging a meet-up as agreed with the buyer.
              </li>
              <li>
                Each listing is limited to <strong>1 unit</strong>. Regular users may have up to <strong>10 active listings</strong> at a time. Verified influencers may have up to <strong>25 active listings</strong>. A listing slot is freed when an item is sold, archived, or deleted.
              </li>
              <li>
                <strong>PR, gifted, and endorsement products</strong> may be sold on {'{'}Beauty{'}'} Stashmart as part of your personal stash, provided:
                (a) there is no contractual term between you and the brand that forbids resale, (b) the product is authentic, and {'(c)'} the product is safe for use and not expired.
              </li>
              <li>
                The following are <strong>not permitted</strong>: items explicitly marked "not for resale" by the brand, pre-release or lab samples,
                and any products where the seller knows their brand agreement or contract prohibits resale. Sellers are solely responsible for ensuring
                they are not violating any agreements with brands by listing such items.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">5. Prohibited Users &amp; Activities</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Official stores, brand distributors, and wholesalers are not permitted</strong> to sell on {'{'}Beauty{'}'} Stashmart.
                This platform is for individuals selling beauty products from their <em>personal stash, PR gifts, or endorsement items</em> &mdash; not for commercial retail operations.
              </li>
              <li>Listing or selling illegal, hazardous, restricted, or regulated substances.</li>
              <li>Manipulating reviews, feedback, or verification status.</li>
              <li>Creating multiple accounts to circumvent platform rules.</li>
              <li>Using the Platform for any fraudulent, deceptive, or unlawful activity.</li>
              <li>
                {'{'}Beauty{'}'} Stashmart employs <strong>automated systems to detect and prevent commercial reselling, bulk listing, and store-like behavior</strong>.
                Accounts found engaging in such activities may have their listings removed and their accounts suspended without prior notice.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">6. Buyer Responsibilities</h2>
            <p>
              Buyers are responsible for reviewing product listings carefully before making a purchase, including reading the product description,
              condition, and expiry information. Buyers should reach out to sellers for clarification before purchasing if they have any questions.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">7. Payments &amp; Fees</h2>
            <p>
              Payments on {'{'}Beauty{'}'} Stashmart are processed through <strong>Duitku</strong>, a licensed payment gateway in Indonesia.
              Available payment methods include Virtual Accounts, QRIS, and convenience store payments (Indomaret/Alfamart). {'{'}Beauty{'}'} Stashmart does not store your
              payment card details. All payment processing is handled securely by Duitku in accordance with their terms of service.
            </p>
            <p className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
              <strong className="text-emerald-700">Platform Fee (Beta — 5%):</strong>{' '}
              <span className="text-[#4A4745]">
                During the beta period, {'{'}Beauty{'}'} Stashmart charges sellers a 5% platform commission on each completed sale, deducted from the seller's payout.
                Buyers are not charged any platform fee — they pay only the product price, payment gateway processing fee, and shipment cost.
                This policy may change in the future, and users will be notified in advance of any fee structure changes.
              </span>
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">8. Account Suspension &amp; Content Removal</h2>
            <p>
              {'{'}Beauty{'}'} Stashmart reserves the right to suspend, restrict, or permanently remove any user account or listing that violates
              these Terms, is reported for fraudulent or deceptive activity, or is found to be in breach of applicable Indonesian law.
              We may take action at our sole discretion and without prior notice.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">9. Intellectual Property</h2>
            <p>
              Users retain ownership of the content they upload (product photos, descriptions). By listing on {'{'}Beauty{'}'} Stashmart, you
              grant us a non-exclusive, royalty-free license to display your content on the Platform for the purpose of operating the marketplace.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">10. Limitation of Liability</h2>
            <p>
              {'{'}Beauty{'}'} Stashmart provides the Platform on an <strong>"as is"</strong> and <strong>"as available"</strong> basis.
              To the fullest extent permitted by law, we disclaim all warranties, express or implied. {'{'}Beauty{'}'} Stashmart shall not be
              liable for any direct, indirect, incidental, consequential, or special damages arising from your use of the Platform,
              any transactions conducted through the Platform, or any products purchased.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">11. Dispute Resolution</h2>
            <p>
              In the event of a dispute between a buyer and seller, we encourage both parties to resolve the matter amicably.
              {'{'}Beauty{'}'} Stashmart may offer mediation assistance at its discretion but is not obligated to intervene. Any unresolved
              disputes shall be governed by the laws of the Republic of Indonesia.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">12. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. Changes will be posted on this page with an updated revision date.
              Continued use of the Platform after changes are posted constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">13. Contact</h2>
            <p>
              If you have questions about these Terms, please contact us at{' '}
              <a href="mailto:hello@stashmart.store" className="text-[#D48585] underline">hello@stashmart.store</a>.
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
